// 給与：月次の支給予定額（純関数・スプリント01／F1）。
// 仕様: docs/spec.md §4 F1・確定事項 D-1〜D-6/D-13/D-14・§2.2「三つの人件費の区別」。
//
// 設計の要（ここを外すと二重の真実ができる）:
//   - 歩合＝当月（積日基準）の自社便 運賃 × 乗務員別歩合率。率の解決は収支エンジンと**同じ**
//     laborRateFor（＝commissionPctAt）を通し、端数も**便ごとに丸めず月合計で四捨五入**する。
//     これにより「歩合乗務員の支給予定額 ＝ 同じ月の収支の人件費」が構造的に ±0 で成立する
//     （片方の結果をもう片方へコピーしない。test_payroll_monthly.mjs が突き合わせで固定）。
//   - 月給＝マスタの monthlySalary（当月の便が0件でも出る）。
//   - 日給＝日額 × 支給対象日数（スプリント02／F2）。日数の既定値は**暦日ベースの稼働日**（D-2）で、
//     収支の営業日ユニオン（operations.js の bizDaysBetween）は**参照しない**（書き換えもしない。J-2）。
//     暦日の算出は payroll/workdays.core.js に分けてある（このファイルは祝日・営業日の判定を持たない）。
//     参考表示用の「収支の稼働日」は呼び出し側が computeOperations の出力（drivers[].days）を
//     opsDays として渡す＝式のコピーを作らず、収支に出ている数字そのものを並べる。
//   - 受注試算（laborQuote の時給・係数・割増）は一切読まない（D-7・§2.2）。
import { normTrip, normTacho, laborRateFor, commissionPctAt, LABOR_RATE } from '../analytics/operations.js'
import { calendarDaysByDriver, tachoRunDays, normDays, MAX_DAYS } from './workdays.core.js'

const pad = (n) => String(n).padStart(2, '0')
const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')
const num = (v) => (v == null || v === '' || isNaN(Number(v))) ? null : Number(v)

// 便の月帰属＝積日（D-5）。運行実績×収支ビュー（operations.js の monthOf）と同じ解決順で、
// 同じ便集合を見ることを保証する（month 列 → [月,日] → ISO日付）。
export function tripMonth(t) {
  if (!t) return ''
  if (t.month) return String(t.month).slice(0, 7)
  if (t.dm) return '2026-' + pad(t.dm[0])
  return t.date ? String(t.date).slice(0, 7) : ''
}

// 便データがある月の一覧（昇順）。取込済みの月はすべて選べる（D-13）。
export function payrollMonths(trips) {
  return [...new Set((trips || []).map(tripMonth).filter(Boolean))].sort()
}

// 乗務員コードの並び（数値コードは数値順・空コードは最後）
function cmpCode(a, b) {
  if (!a.code) return 1
  if (!b.code) return -1
  const na = Number(a.code), nb = Number(b.code)
  if (!isNaN(na) && !isNaN(nb)) return na - nb
  return String(a.code).localeCompare(String(b.code), 'ja')
}

// 給与区分（3区分・D-1）: 歩合 / 月給 / 日給
function wageTypeOf(m) {
  if (!m || (m.wageType || 'commission') !== 'fixed') return 'commission'
  return (m.fixedKind || 'monthly') === 'daily' ? 'daily' : 'monthly'
}

/**
 * 選択月の乗務員別 支給予定額を算出する。
 * @param {object[]} trips  便（全月ぶんで可。month で絞る）
 * @param {object[]} tachos デジタコ（氏名の解決にだけ使う。金額には一切効かない）
 * @param {string}   month  'YYYY-MM'
 * @param {object|null} drivers 乗務員マスタ（コード→doc。収支エンジンへ渡すものと同一オブジェクト）
 * @param {number}   estimateRate 試算用率（小数。個別設定が無い乗務員のフォールバック）
 * @param {object|null} dayInputs 日給の支給対象日数（コード→日数）。その月ぶんだけを渡す（F2・D-13）
 * @param {object|null} opsDays 収支の稼働日（コード→営業日数）。参考表示用。渡さなければ null＝「—」
 */
export function computePayrollMonth({ trips, tachos = [], month, drivers = null, estimateRate = null, dayInputs = null, opsDays = null }) {
  const est = (estimateRate != null && !isNaN(Number(estimateRate))) ? Number(estimateRate) : LABOR_RATE
  const monthTrips = (trips || []).filter((t) => tripMonth(t) === month)
  const all = monthTrips.map(normTrip)
  const self = all.filter((t) => t.kind === 'own') // 傭車便は歩合の対象外（D-4）

  // 氏名の解決は収支の乗務員別と同じ順（マスタ → デジタコ → 便の乗務員名 → コード）。
  // 収支画面と同じラベルで並ぶ＝評価者が両画面を突き合わせられる。
  const names = {}
  for (const k in (drivers || {})) { const m = drivers[k]; if (m && m.name) names[normCode(m.code || k)] = m.name }
  const nameByCode = {}
  for (const t of (tachos || []).map(normTacho)) if (t.driverCode && t.driverName && !nameByCode[t.driverCode]) nameByCode[t.driverCode] = t.driverName
  const hint = {}
  for (const t of self) if (t.driverName && !hint[t.driverCode]) hint[t.driverCode] = t.driverName
  const nmOf = (code) => names[code] || nameByCode[code] || hint[code] || code || '(乗務員コード未設定)'

  // 日数の材料（F2）。暦日ユニオン＝既定値、収支の稼働日・デジタコ運行日＝参考表示。
  const calByCode = calendarDaysByDriver(self)
  const tachoByCode = tachoRunDays(tachos, month)
  const dayByCode = {}
  for (const k in (dayInputs || {})) { const c = normCode(k); if (c) dayByCode[c] = dayInputs[k] }
  const bizByCode = {}
  for (const k in (opsDays || {})) { const c = normCode(k); if (c) bizByCode[c] = opsDays[k] }
  const daysCtx = { calByCode, tachoByCode, dayByCode, bizByCode, hasOps: !!opsDays }

  // 便の集計（コード別）。歩合額は**便ごとに丸めず**生値で積む（D-6）。
  const agg = {}
  const ens = (code) => agg[code] || (agg[code] = { code, fare: 0, raw: 0, n: 0, pcts: new Set(), unset: false })
  for (const t of self) {
    const a = ens(t.driverCode)
    const rate = laborRateFor(drivers, t.driverCode, t.dLoad, est) // 収支エンジンと同じ率解決
    const pct = drivers ? commissionPctAt(drivers[normCode(t.driverCode)], t.dLoad) : null
    if (pct == null) a.unset = true
    a.pcts.add(Math.round(rate * 10000) / 100)
    a.fare += t.fare
    a.raw += t.fare * rate
    a.n++
  }

  const rows = []
  const seen = new Set()
  for (const code in agg) {
    const a = agg[code]
    const m = (drivers && drivers[normCode(code)]) || null
    rows.push(makeRow({ a, m, code, name: nmOf(code), est, daysCtx }))
    seen.add(code)
  }
  // 当月の便が無い固定給の在籍者も並べる（月給はその月に便が0件でも月額が出る）。
  // 退職者（status=inactive）は便がある月だけ出す＝入退社日での厳密な絞り込みは F9（スプリント05）。
  for (const k in (drivers || {})) {
    const m = drivers[k]
    const code = normCode(m && m.code ? m.code : k)
    if (!m || seen.has(code)) continue
    if (wageTypeOf(m) === 'commission') continue
    if ((m.status || 'active') === 'inactive') continue
    rows.push(makeRow({ a: null, m, code, name: nmOf(code), est, daysCtx }))
    seen.add(code)
  }
  rows.sort(cmpCode)

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0)
  return {
    month,
    rows,
    total,
    noTrips: monthTrips.length === 0,
    tripCount: self.length,
    unsetRateCount: rows.filter((r) => r.wageType === 'commission' && r.rateUnset).length,
    pendingCount: rows.filter((r) => r.amount == null).length,
    // 日数未入力の日給乗務員（＝月次合計に額が入っていない人数。合計行の脇に注記する）
    dailyPendingCount: rows.filter((r) => r.wageType === 'daily' && r.needsDays).length,
    counts: {
      commission: rows.filter((r) => r.wageType === 'commission').length,
      monthly: rows.filter((r) => r.wageType === 'monthly').length,
      daily: rows.filter((r) => r.wageType === 'daily').length,
    },
    estimatePct: Math.round(est * 10000) / 100,
  }
}

function makeRow({ a, m, code, name, est, daysCtx }) {
  const wageType = wageTypeOf(m)
  const pcts = a ? [...a.pcts].sort((x, y) => x - y) : []
  const row = {
    code,
    name,
    wageType,
    status: (m && m.status) || 'active',
    fare: a ? Math.round(a.fare) : 0,
    tripCount: a ? a.n : 0,
    ratePcts: pcts,
    ratePctUsed: pcts.length === 1 ? pcts[0] : null,
    rateUnset: false,
    needsDays: false,
    monthlySalary: m ? num(m.monthlySalary) : null,
    dailyWage: m ? num(m.dailyWage) : null,
    amount: null,
    warn: [],
  }
  // 日数の材料（F2）。全区分に載せる＝評価者が「暦日 vs 収支の稼働日」をどの行でも突き合わせられる。
  const ctx = daysCtx || { calByCode: {}, tachoByCode: {}, dayByCode: {}, bizByCode: {}, hasOps: false }
  const cal = ctx.calByCode[code] || []
  row.calDays = cal.length // 暦日ベースの稼働日（土日祝を含む。D-2）＝日給の既定値の出所
  row.calDates = cal
  row.bizDays = ctx.hasOps ? (ctx.bizByCode[code] || 0) : null // 収支に出ている稼働日（営業日ユニオン）。無ければ「—」
  row.weekendDays = row.bizDays == null ? null : Math.max(0, row.calDays - row.bizDays) // 土日祝に走った日数
  row.tachoDays = ctx.tachoByCode[code] != null ? ctx.tachoByCode[code] : null // デジタコ運行日（未取込は null＝「—」）
  row.defaultDays = Math.min(row.calDays, MAX_DAYS) // 既定値（未確定）。暦月に無い日数まで既定にはしない
  row.days = null
  row.daysSaved = false
  if (!code) row.warn.push('乗務員コード未設定')
  if (wageType === 'commission') {
    // 歩合: 便ごとに丸めず、月合計で四捨五入（D-6）＝収支の人件費と同じ端数処理
    row.amount = a ? Math.round(a.raw) : 0
    row.rateUnset = a ? a.unset : true
    if (row.rateUnset) {
      row.ratePctUsed = Math.round(est * 10000) / 100
      row.warn.push(`率未設定（試算用率 ${row.ratePctUsed}% で計算）`)
    }
  } else if (wageType === 'monthly') {
    row.amount = row.monthlySalary
    if (row.amount == null) row.warn.push('月額未設定')
  } else {
    // 日給（F2）: 額 ＝ 日額 × 支給対象日数。日数は経理が入れた確定値だけを使い、
    // 暦日ベースの既定値（defaultDays）は「未確定の提案」に留める＝勝手に額を作らない。
    const d = normDays(ctx.dayByCode[code])
    row.days = d
    row.daysSaved = d != null
    row.needsDays = d == null
    row.amount = (d != null && row.dailyWage != null) ? Math.round(row.dailyWage * d) : null
    if (d == null) row.warn.push('日数未入力')
    if (row.dailyWage == null) row.warn.push('日額未設定')
  }
  return row
}
